import {makeValuer} from './net-worth';
import {balance,cents,type Row} from './finance';
import {liabilityKinds} from './accounts';
type Tx=Row<'transactions'>;

/** Rows a spending report counts: posted, live, not transfers, not excluded from reports. */
const reportable=(t:Tx)=>t.status!=='pending'&&!t.deleted_at&&!t.is_transfer&&!t.excluded_from_reports;
/** Spending is money out; refunds and reimbursements are money back and reduce it. */
const isSpending=(t:Tx)=>t.amount<0||t.is_refund||t.is_reimbursement;

export type CategorySpend={id:string;name:string;icon:string|null;color:string|null;cents:number};
/**
 * A month's spending by category, largest first. Split transactions count each part in
 * its own category. Categories whose refunds cancel their spending are left out.
 */
export function spendByCategory(tx:Tx[],splits:Row<'transaction_splits'>[],categories:Row<'categories'>[],month:string){
 const parts=new Map<string,Row<'transaction_splits'>[]>();
 for(const s of splits){const list=parts.get(s.transaction_id)??[];list.push(s);parts.set(s.transaction_id,list)}
 const by=new Map<string,number>();
 const add=(category:string|null,amount:number)=>{const key=category??'none';by.set(key,(by.get(key)??0)-cents(amount))};
 for(const t of tx){
  if(!reportable(t)||!isSpending(t)||!t.transaction_date.startsWith(month))continue;
  const own=parts.get(t.id);
  if(own?.length)for(const p of own)add(p.category_id,p.amount);else add(t.category_id,t.amount);
 }
 const named=new Map(categories.map(c=>[c.id,c]));
 const rows:CategorySpend[]=[...by].filter(([,v])=>v>0).map(([id,v])=>{const c=named.get(id);return {id,name:c?.name??'Uncategorized',icon:c?.icon??null,color:c?.color??null,cents:v}});
 rows.sort((a,b)=>b.cents-a.cents||a.name.localeCompare(b.name));
 return {rows,total:rows.reduce((s,r)=>s+r.cents,0)};
}

const daysIn=(month:string)=>{const [y,m]=month.split('-').map(Number);return new Date(Date.UTC(y,m,0)).getUTCDate()};
export const shiftMonth=(month:string,by:number)=>{const [y,m]=month.split('-').map(Number);const d=new Date(Date.UTC(y,m-1+by,1));return d.toISOString().slice(0,7)};
/** Cumulative spending in cents at the end of each day of a month (index 0 is day 1). */
function cumulative(tx:Tx[],month:string){
 const daily=new Array(daysIn(month)).fill(0);
 for(const t of tx)if(reportable(t)&&isSpending(t)&&t.transaction_date.startsWith(month))daily[Number(t.transaction_date.slice(8,10))-1]-=cents(t.amount);
 let run=0;return daily.map(v=>run+=v);
}
/**
 * This month's cumulative spending up to today, against the average cumulative spending of
 * the previous three months at the same day of the month. A shorter month counts its final
 * total for the days it lacks.
 */
export function spendThisMonth(tx:Tx[],today:string){
 const month=today.slice(0,7);const day=Number(today.slice(8,10));const length=daysIn(month);
 const current=cumulative(tx,month).slice(0,day);
 const previous=[1,2,3].map(n=>cumulative(tx,shiftMonth(month,-n)));
 const average=Array.from({length},(_,i)=>Math.round(previous.reduce((s,m)=>s+m[Math.min(i,m.length-1)],0)/previous.length));
 const spent=current.at(-1)??0;const averageToDate=average[day-1]??0;
 return {month,day,length,current,average,spent,averageToDate,difference:spent-averageToDate};
}

export type AccountSlice={id:string;name:string;cents:number};
/**
 * Balances of active asset accounts, largest first. A donut can only show parts of a whole,
 * so loans and cards are left out and reported separately as the amount owed.
 */
export function accountBreakdown(accounts:Row<'accounts'>[],tx:Tx[],valuations:Row<'account_valuations'>[]=[],date?:string){
 // Recorded values count here as in net worth: a home or brokerage at its latest value.
 const value=makeValuer(accounts,tx,valuations);const on=(a:Row<'accounts'>)=>date?value(a,date):balance(a,tx);
 const active=accounts.filter(a=>!a.is_archived);
 const assets:AccountSlice[]=active.filter(a=>!liabilityKinds.has(a.kind)).map(a=>({id:a.id,name:a.name,cents:on(a)})).filter(a=>a.cents>0).sort((a,b)=>b.cents-a.cents||a.name.localeCompare(b.name));
 const owed=active.filter(a=>liabilityKinds.has(a.kind)).reduce((s,a)=>s+Math.max(0,on(a)),0);
 return {assets,total:assets.reduce((s,a)=>s+a.cents,0),owed};
}

/** Compact currency for axis labels: $950, $1.4k, $40k, $1.2M. */
export function compactMoney(centsValue:number){
 const d=Math.abs(centsValue)/100;const sign=centsValue<0?'-':'';
 if(d>=1_000_000)return `${sign}$${+(d/1_000_000).toFixed(1)}M`;
 if(d>=1_000)return `${sign}$${+(d/1_000).toFixed(d>=10_000?0:1)}k`;
 return `${sign}$${Math.round(d)}`;
}

/** Keeps the largest series and folds the rest into one "Other" row, so no series needs an invented color. */
export function foldSeries<T extends {id:string;name:string;cents:number}>(rows:T[],max:number):(T|{id:'other';name:string;cents:number})[]{
 if(rows.length<=max)return rows;
 const kept=rows.slice(0,max-1);const rest=rows.slice(max-1);
 return [...kept,{id:'other',name:`Other (${rest.length})`,cents:rest.reduce((s,r)=>s+r.cents,0)}];
}
